// IntegrityHealthPanel.jsx — Integrity Connect + AI Health Transcription
// (PR-6). Lives in the LeadDetail right rail alongside SendToIntegrityButton.
//
// Behaviour rules that the UI enforces (matched by health*Route tests):
//   - Panel renders NOTHING until a HealthExtraction exists for the lead —
//     no false positive that we're capturing health data on a lead that
//     hasn't had a call yet.
//   - Every row shows editable text with a check (confirm) and X (reject)
//     button. Confirmed rows get a green pill; rejected rows are struck
//     through. Both are locked once pushedAt is set (server enforces too).
//   - The "Push to Integrity" button is disabled unless AT LEAST one
//     row (or vitals) is confirmed AND unpushed AND the lead has an
//     IntegrityLeadLink for the selected NPN. If the link is missing,
//     the button surfaces the exact remediation ("Push lead first").
//   - Vitals is one block (bp, hr, height, weight, smoker) not an array.
//
// This is display + POST plumbing only. The extractor and push service do
// the work — we never call OpenAI or Integrity from the browser.

import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost } from '../../lib/api.js';
import { getSocket } from '../../lib/socket.js';

const KIND_LABEL = {
  conditions:    'Health Conditions',
  prescriptions: 'Prescriptions',
  history:       'Family / History',
};

function StatusPill({ row }) {
  if (row.pushedAt)   return <span className="pill pill-pushed">Pushed</span>;
  if (row.rejectedAt) return <span className="pill pill-rejected">Rejected</span>;
  if (row.confirmedBy) return <span className="pill pill-confirmed">Confirmed</span>;
  return <span className="pill pill-pending">Pending</span>;
}

function Row({ row, kind, leadId, onChanged, disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(row.editedText || row.text || '');
  const [busy, setBusy] = useState(false);

  async function act(action) {
    setBusy(true);
    try {
      const url = `/integrity/leads/${leadId}/health/rows/${kind}/${row._id}/${action}`;
      const body = action === 'confirm' && editing ? { editedText: draft } : {};
      const res = await apiPost(url, body);
      if (res?.ok && res.row) onChanged(kind, res.row);
    } finally {
      setBusy(false);
      setEditing(false);
    }
  }

  const locked = disabled || !!row.pushedAt;

  return (
    <div className={`health-row health-row--${kind}`}>
      <div className="health-row-body">
        {editing ? (
          <input
            className="health-row-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={locked}
          />
        ) : (
          <span className={row.rejectedAt ? 'health-row-text struck' : 'health-row-text'}>
            {row.editedText || row.text}
          </span>
        )}
        {row.severity && <span className="health-row-sev">· {row.severity}</span>}
        {row.dosage && <span className="health-row-dose">· {row.dosage}</span>}
        <StatusPill row={row} />
      </div>
      <div className="health-row-actions">
        {!locked && (
          <>
            {!editing && (
              <button className="btn-icon" title="Edit" onClick={() => setEditing(true)} disabled={busy}>
                Edit
              </button>
            )}
            <button className="btn-icon btn-confirm" title="Confirm" onClick={() => act('confirm')} disabled={busy}>
              ✓
            </button>
            <button className="btn-icon btn-reject" title="Reject" onClick={() => act('reject')} disabled={busy}>
              ✗
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function VitalsBlock({ vitals, vitalsConfirmedAt, leadId, onChanged, disabled }) {
  const [busy, setBusy] = useState(false);
  if (!vitals || (!vitals.bloodPressure && !vitals.heartRate && !vitals.heightCm && !vitals.weightKg && vitals.smoker == null)) {
    return null;
  }
  async function act(action) {
    setBusy(true);
    try {
      const res = await apiPost(`/integrity/leads/${leadId}/health/vitals/${action}`, {});
      if (res?.ok) onChanged('vitals', { vitals: res.vitals, vitalsConfirmedAt: res.vitalsConfirmedAt });
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="health-vitals">
      <h5>Vitals</h5>
      <ul>
        {vitals.bloodPressure && <li>BP: {vitals.bloodPressure}</li>}
        {vitals.heartRate && <li>HR: {vitals.heartRate}</li>}
        {vitals.heightCm && <li>Height: {vitals.heightCm} cm</li>}
        {vitals.weightKg && <li>Weight: {vitals.weightKg} kg</li>}
        {vitals.smoker != null && <li>Smoker: {vitals.smoker ? 'Yes' : 'No'}</li>}
      </ul>
      <div className="health-row-actions">
        {vitalsConfirmedAt ? (
          <span className="pill pill-confirmed">Confirmed</span>
        ) : (
          <>
            <button className="btn-icon btn-confirm" onClick={() => act('confirm')} disabled={busy || disabled}>Confirm vitals</button>
            <button className="btn-icon btn-reject"  onClick={() => act('reject')}  disabled={busy || disabled}>Clear</button>
          </>
        )}
      </div>
    </div>
  );
}

export default function IntegrityHealthPanel({ leadId, defaultNpn }) {
  const [extraction, setExtraction] = useState(null);
  const [links, setLinks] = useState([]);
  const [selectedNpn, setSelectedNpn] = useState(defaultNpn || '');
  const [loading, setLoading] = useState(true);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [error, setError] = useState('');

  async function refresh() {
    try {
      const [ext, linkRes] = await Promise.all([
        apiGet(`/integrity/leads/${leadId}/health/current`),
        apiGet(`/integrity/leads/${leadId}/link`),
      ]);
      if (ext?.ok) setExtraction(ext.extraction);
      if (linkRes?.ok) {
        setLinks(linkRes.links || []);
        if (!selectedNpn && linkRes.links?.length) {
          setSelectedNpn(linkRes.links[0].npn || '');
        }
      }
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!leadId) return;
    refresh();
    // Subscribe to tenant-scoped extractor updates for this lead.
    const socket = getSocket();
    const onUpdate = (payload) => {
      if (!payload || String(payload.lead) !== String(leadId)) return;
      // Payload IS the serialised extraction from the extractor service.
      setExtraction(payload.extraction || payload);
    };
    if (socket?.on) socket.on('health:extraction-updated', onUpdate);
    return () => {
      if (socket?.off) socket.off('health:extraction-updated', onUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  function onRowChanged(kind, row) {
    setExtraction((prev) => {
      if (!prev) return prev;
      if (kind === 'vitals') {
        return { ...prev, vitals: row.vitals, vitalsConfirmedAt: row.vitalsConfirmedAt };
      }
      return {
        ...prev,
        [kind]: (prev[kind] || []).map((r) => (String(r._id) === String(row._id) ? row : r)),
      };
    });
  }

  const link = useMemo(() => links.find((l) => l.npn === selectedNpn) || null, [links, selectedNpn]);

  const confirmedUnpushed = useMemo(() => {
    if (!extraction) return 0;
    const count = (arr) => (arr || []).filter((r) => r.confirmedBy && !r.rejectedAt && !r.pushedAt).length;
    return count(extraction.conditions) + count(extraction.prescriptions) + count(extraction.history);
  }, [extraction]);

  async function pushAll() {
    if (!selectedNpn) return;
    setPushing(true);
    setPushResult(null);
    try {
      const res = await apiPost(`/integrity/leads/${leadId}/health/push`, { npn: selectedNpn });
      if (res?.ok) {
        setPushResult(res.result);
        refresh();
      } else {
        setError(res?.error || 'push failed');
      }
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setPushing(false);
    }
  }

  if (loading) return <div className="integrity-health-panel loading">Loading health capture…</div>;
  if (!extraction) return null;   // don't render until an extraction exists

  const noLink = !link || !link.integrityLeadId;
  const canPush = !noLink && confirmedUnpushed > 0 && !pushing;

  return (
    <div className="integrity-health-panel">
      <header className="integrity-health-panel-header">
        <h4>Health capture (AI)</h4>
        <span className="muted">
          Last extract: {extraction.lastExtractedAt ? new Date(extraction.lastExtractedAt).toLocaleTimeString() : '—'}
          {' · '}Passes: {extraction.extractionCount || 0}
        </span>
      </header>

      {links.length > 1 && (
        <label className="health-npn-picker">
          Push under NPN:
          <select value={selectedNpn} onChange={(e) => setSelectedNpn(e.target.value)}>
            {links.map((l) => (
              <option key={l.npn} value={l.npn}>{l.npn} ({l.status || 'linked'})</option>
            ))}
          </select>
        </label>
      )}

      {['conditions', 'prescriptions', 'history'].map((kind) => (
        <section key={kind} className="health-section">
          <h5>{KIND_LABEL[kind]}</h5>
          {(extraction[kind] || []).length === 0 ? (
            <div className="muted small">Nothing extracted yet.</div>
          ) : (
            (extraction[kind] || []).map((row) => (
              <Row
                key={String(row._id)}
                row={row}
                kind={kind}
                leadId={leadId}
                onChanged={onRowChanged}
                disabled={pushing}
              />
            ))
          )}
        </section>
      ))}

      <VitalsBlock
        vitals={extraction.vitals}
        vitalsConfirmedAt={extraction.vitalsConfirmedAt}
        leadId={leadId}
        onChanged={onRowChanged}
        disabled={pushing}
      />

      <footer className="integrity-health-panel-footer">
        {noLink && (
          <div className="warn small">
            Push lead to Integrity first, then confirmed rows can be sent to the Health Profile.
          </div>
        )}
        <button className="btn btn-primary" onClick={pushAll} disabled={!canPush}>
          {pushing
            ? 'Pushing…'
            : `Push ${confirmedUnpushed} confirmed row${confirmedUnpushed === 1 ? '' : 's'} to Integrity`}
        </button>
        {pushResult && (
          <div className="push-result small">
            Conditions: {pushResult.conditions.pushed.length} pushed, {pushResult.conditions.failed.length} failed, {pushResult.conditions.skipped.length} skipped ·
            Prescriptions: {pushResult.prescriptions.pushed.length} pushed, {pushResult.prescriptions.failed.length} failed, {pushResult.prescriptions.skipped.length} skipped
          </div>
        )}
        {error && <div className="error small">{error}</div>}
        <div className="muted small">
          AI-extracted health data is never sent to Integrity without explicit confirmation.
        </div>
      </footer>
    </div>
  );
}
